import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { processChoiceValues } from 'crm/_newComponents/FastFilters/helpers';
import { setModifiedFastFilters, getCRMCustomFilter } from 'crm/_redux/actions';

import InputIntegerChoice from './_components/InputIntegerChoice';
import InputDate from './_components/InputDate';
import InputStr from './_components/InputStr';
import { staticStyles } from './style';
import InputRange from './_components/InputRange';

const FastFilters = ({ fastFiltersModified, onChange, getFastFiltersFunc, fastFiltersSelector }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isFirstLaunch, setIsFirstLaunch] = useState(true);
  const fastFilters = useSelector(fastFiltersSelector);
  const currentCustomFilter = useSelector(state => state.crm.common.customFilter);

  const currentUrl = useMemo(() => location.search.replace('?', ''), [location.search]);
  const customFilterIdParam = queryString.parse(currentUrl).filterId;

  useEffect(() => {
    if (Object.keys(fastFilters).length !== 0) {
      // 使用 URL 参数来初始化筛选状态，确保与 URL 同步；传入当前 modifiedFilters 以保留用户勾选的展示状态
      const urlParams = queryString.parse(location.search);
      const modifiedFilters = processChoiceValues(fastFilters, urlParams, currentCustomFilter, fastFiltersModified);

      dispatch(setModifiedFastFilters(modifiedFilters));
    }
  }, [fastFilters, currentCustomFilter, location.search]);

  useEffect(() => {
    dispatch(getFastFiltersFunc.request());
  }, [dispatch]);

  useEffect(() => {
    if (customFilterIdParam) {
      dispatch(getCRMCustomFilter.request({ id: customFilterIdParam }));
    }
  }, [customFilterIdParam]);

  // 首次加载时，不再从 sessionStorage 恢复 URL
  // 直接使用当前 URL 参数，避免后退时恢复错误的筛选状态
  useEffect(() => {
    if (isFirstLaunch) {
      setIsFirstLaunch(false);
    }
  }, [isFirstLaunch]);

  const onInputChange = filterName => inputValue => {
    dispatch(
      setModifiedFastFilters({
        ...fastFiltersModified,
        [filterName]: {
          ...fastFiltersModified[filterName],
          choiceValuesAfterSearch: fastFiltersModified[filterName].choiceValues.filter(it =>
            it.name.toLowerCase().includes(inputValue.toLowerCase())
          ),
        },
      })
    );
  };

  return (
    <div className="FastFilters">
      {Object.keys(fastFiltersModified)
        .filter(filterName => fastFiltersModified[filterName].isDisplayed)
        .map(filterName => {
          const currentFastFilter = fastFiltersModified[filterName];
          const withInput =
            currentFastFilter.inputType === 'inputIntegerChoice' || currentFastFilter.inputType === 'inputStringChoice';
          if (
            currentFastFilter.inputType === 'inputIntegerChoice' ||
            currentFastFilter.inputType === 'inputStringChoice' ||
            currentFastFilter.inputType === 'inputBoolean'
          ) {
            return (
              <InputIntegerChoice
                isDisabled={!!customFilterIdParam}
                key={`fastFilter-${filterName}`}
                onInputChange={withInput ? onInputChange(filterName) : () => {}}
                options={
                  withInput
                    ? fastFiltersModified[filterName].choiceValuesAfterSearch
                    : fastFiltersModified[filterName].choiceValues
                }
                name={filterName}
                multi={currentFastFilter.inputType !== 'inputBoolean'}
                isNew={
                  filterName === 'accountTypeContactFilter' ||
                  filterName === 'tagContactFilter' ||
                  filterName === 'clientTypeContactFilter'
                }
                withInput={withInput}
                onSelect={value => {
                  onChange(filterName, currentFastFilter.inputType, value);
                }}
              />
            );
          }

          if (currentFastFilter.inputType === 'inputDate') {
            return (
              <InputDate
                isDisabled={!!customFilterIdParam}
                key={`fastFilter-${filterName}`}
                options={fastFiltersModified[filterName].choiceValues}
                name={filterName}
                onSelect={value => {
                  onChange(filterName, currentFastFilter.inputType, value);
                }}
              />
            );
          }

          if (currentFastFilter.inputType === 'inputStr') {
            return (
              <InputStr
                isDisabled={!!customFilterIdParam}
                key={`fastFilter-${filterName}`}
                name={filterName}
                value={currentFastFilter.value}
                onChange={value => {
                  onChange(filterName, currentFastFilter.inputType, value);
                }}
              />
            );
          }

          if (currentFastFilter.inputType === 'inputRange') {
            return (
              <InputRange
                isDisabled={!!customFilterIdParam}
                key={`fastFilter-${filterName}`}
                name={filterName}
                enteredValues={currentFastFilter.enteredValues}
                onChange={enteredValues => {
                  onChange(filterName, currentFastFilter.inputType, enteredValues);
                }}
              />
            );
          }

          return '';
        })}
      <style jsx>{staticStyles}</style>
    </div>
  );
};

FastFilters.propTypes = {
  fastFiltersModified: PropTypes.object.isRequired,
  getFastFiltersFunc: PropTypes.object.isRequired,
  fastFiltersSelector: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default FastFilters;
